import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router'
import { z } from 'zod'
import { parseSearchParams } from 'zod-search-params'
import { type Route } from './+types/route'
import { ActionButton } from '~/components/action-button.tsx'
import { ArticleHeader } from '~/components/article-header.tsx'
import { Articles } from '~/components/articles.tsx'
import { Cols } from '~/components/cols.tsx'
import { Empty } from '~/components/empty.tsx'
import { GeneralErrorBoundary } from '~/components/error-boundary.tsx'
import { GradientsContainer } from '~/components/gradients-container.tsx'
import { ErrorNotFound } from '~/components/ui/errors/error-not-found'
import { postsPerPage } from '~/routes/resources.blog-posts.ts'
import { blogPostsNormalize } from '~/utils/blog.ts'
import { getFormatter } from '~/utils/date-formatter.server.ts'
import { api } from '~/utils/gql-client.server.ts'
import { generateMeta, type MetaProps, validateSeo } from '~/utils/meta.ts'
import { invariantResponse } from '~/utils/misc.ts'
import { getPageState } from '~/utils/page-state.ts'

export function meta({ data }: Route.MetaArgs) {
  return data?.metaProps
    ? generateMeta(data.metaProps as MetaProps)
    : [{ title: '404 not found' }]
}

export let handle = {
  i18n: 'blog',
}

export async function loader({ request, params }: Route.LoaderArgs) {
  const { start } = parseSearchParams(
    z.object({
      start: z.number().optional().catch(0),
    }),
    new URL(request.url).searchParams,
  )

  const { blogTagBySlug } = await api.BlogTagBySlug({
    slug: params.slug,
  })

  invariantResponse(blogTagBySlug, 'Tag not found', { status: 404 })

  const { blogPosts } = await api.BlogPosts({
    sort: 'publishDate:desc',
    limit: postsPerPage,
    start: start ?? 0,
    where: JSON.parse(JSON.stringify({ blog_tags: blogTagBySlug.id })),
  })

  const metaProps: MetaProps = {
    seo: await validateSeo(blogTagBySlug.SEO),
    localizations: blogTagBySlug.localizations?.data.map(
      (a) => a.attributes?.locale,
    ) as string[],
    pageState: await getPageState(request),
  }

  const formatter = await getFormatter(request)

  return {
    metaProps: metaProps,
    blogPosts: blogPostsNormalize({ formatter, posts: blogPosts }),
    blogTagBySlug,
  }
}

export default function BlogTag({ loaderData }: Route.ComponentProps) {
  const { blogTagBySlug, blogPosts } = loaderData
  const { t } = useTranslation(['blog'])
  const [posts, setPosts] = useState(blogPosts)
  const [endReached, setEndReached] = useState(false)
  const fetcher = useFetcher<typeof loader>()
  const loading = fetcher.state === 'loading'

  useEffect(() => {
    setEndReached(false)
    setPosts(blogPosts)
  }, [blogTagBySlug, blogPosts])

  useEffect(() => {
    if (!fetcher.data || loading) {
      return
    }

    if (fetcher.data) {
      const newPosts = fetcher.data.blogPosts

      if (!newPosts || newPosts.length === 0) {
        setEndReached(true)
        return
      }
      setPosts((prevPosts) => [...prevPosts, ...newPosts])
    }
  }, [loading, blogPosts, fetcher.data, fetcher.state])

  const loadMore = async () => {
    const start = posts.length
    const query = `?index&start=${start}`
    await fetcher.load(query)
  }

  return (
    <>
      <ArticleHeader title={blogTagBySlug.title} />
      <GradientsContainer>
        <div className="container">
          <Cols size="10">
            {posts.length === 0 && <Empty message={t('blog_posts_empty')} />}
            {posts.length > 0 && (
              <div className="flex flex-col gap-y-10 pt-12 pb-16">
                {posts.length > 0 && <Articles articles={posts} />}
                <div className="pt-16 text-center">
                  {!endReached && (
                    <ActionButton
                      size="big"
                      onClick={loadMore}
                      state={loading ? 'loading' : undefined}
                    >
                      {t('load_more_button')}
                    </ActionButton>
                  )}
                </div>
              </div>
            )}
          </Cols>
        </div>
      </GradientsContainer>
    </>
  )
}

export function ErrorBoundary() {
  const { t } = useTranslation(['blog'])
  return (
    <GeneralErrorBoundary
      statusHandlers={{
        404: () => <ErrorNotFound description={t('tag_not_found')} />,
      }}
    />
  )
}
